import { data, Form, Link, redirect } from 'react-router'
import { getUserId } from '#app/utils/auth.server.ts'
import { SITE_NAME } from '#app/utils/branding.ts'
import { getActiveTest } from '#app/modules/test-management/index.ts'
import { type Route } from './+types/index.ts'

export async function loader({ request }: Route.LoaderArgs) {
	const userId = await getUserId(request)
	if (userId) {
		throw redirect('/dashboard')
	}

	const activeTest = await getActiveTest()
	const totalQuestions = activeTest.sections.reduce(
		(sum, section) => sum + section.questions.length,
		0,
	)

	return { totalQuestions }
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const code = String(formData.get('code') ?? '').trim()
	if (!code) {
		return data({ error: 'Enter an access code to continue.' }, { status: 400 })
	}
	return redirect(`/start?code=${encodeURIComponent(code)}`)
}

export const meta: Route.MetaFunction = () => [
	{ title: `${SITE_NAME} — You Are a Living Ikon` },
	{
		name: 'description',
		content:
			'See beyond a flat personality profile. The IKON Examen separates temperament, potential, and character into three dimensions so you can understand yourself as a living ikon.',
	},
]

const dimensions = [
	{
		id: 'temperament',
		kicker: '01 Temperament',
		label: 'How You Are',
		description: 'What you were born with — your given nature',
	},
	{
		id: 'potential',
		kicker: '02 Potential',
		label: 'What You Carry',
		description: 'What you were gifted with — your natural capacities',
	},
	{
		id: 'character',
		kicker: '03 Character',
		label: 'How You Were Formed',
		description: 'What was shaped in you — through upbringing and response',
	},
] as const

export default function IndexRoute({ loaderData }: Route.ComponentProps) {
	const { totalQuestions } = loaderData

	return (
		<main className="min-h-screen px-6 py-16 md:py-28 bg-cathedral-page">
			<div className="mx-auto flex w-full max-w-5xl flex-col gap-20 md:gap-24">
				{/* Nav */}
				<div className="flex items-center justify-between gap-4">
					<div className="cathedral-heading text-cathedral-brown text-base tracking-[0.12em] uppercase">
						{SITE_NAME}
					</div>
					<div className="flex items-center gap-3">
						<Link
							to="/login"
							className="cathedral-heading text-cathedral-gold hover:text-cathedral-gold-light rounded-sm border border-cathedral-gold/30 px-4 py-2 text-sm tracking-[0.14em] uppercase transition-colors"
						>
							Log In
						</Link>
						<Link
							to="/signup"
							className="cathedral-body text-cathedral-gold hover:text-cathedral-gold-light text-base underline"
						>
							Sign up
						</Link>
					</div>
				</div>

				{/* Hero */}
				<header className="mx-auto max-w-3xl text-center">
					<p className="cathedral-heading text-cathedral-gold text-xs tracking-[0.16em] uppercase">
						A Three-Dimensional Framework
					</p>
					<h1 className="cathedral-heading text-cathedral-dark mt-4 text-5xl font-semibold tracking-wide md:text-7xl">
						You Are a Living Ikon
					</h1>
					<p className="cathedral-body text-cathedral-brown mt-7 text-xl leading-relaxed md:text-2xl md:leading-relaxed">
						Most personality tools stop at self-description. The {SITE_NAME}{' '}
						invites you further: to see your life in light of the One who made
						you, and from there to understand yourself more truthfully. This is
						not self-obsession, but reorientation toward God&apos;s love and design,
						even where sin has distorted the image.
					</p>
				</header>

				<div
					className="mx-auto flex w-full max-w-xl items-center gap-4"
					aria-hidden
				>
					<div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
					<div className="bg-cathedral-bright h-2.5 w-2.5 rotate-45" />
					<div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
				</div>

				{/* Three Dimensions */}
				<section aria-label="Assessment dimensions">
					<ol className="grid gap-4 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:items-stretch md:gap-5">
						{dimensions.flatMap((dimension, index) => {
							const items = [
								<li
									key={dimension.id}
									className="flex flex-col rounded-lg border bg-cathedral-card px-6 py-9 text-center"
								>
									<p className="cathedral-heading text-cathedral-gold text-sm tracking-[0.14em] uppercase">
										{dimension.kicker}
									</p>
									<p className="cathedral-heading text-cathedral-dark mt-3 text-xl font-semibold leading-tight md:text-2xl">
										{dimension.label}
									</p>
									<p className="cathedral-body text-cathedral-brown mt-3 text-base leading-relaxed md:text-lg">
										{dimension.description}
									</p>
								</li>,
							]
							if (index < dimensions.length - 1) {
								items.push(
									<li
										key={`${dimension.id}-arrow`}
										aria-hidden
										className="cathedral-heading text-cathedral-gold hidden self-center text-center text-xl md:block"
									>
										→
									</li>,
								)
							}
							return items
						})}
					</ol>
				</section>

				<div
					className="mx-auto flex w-full max-w-xl items-center gap-4"
					aria-hidden
				>
					<div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
					<div className="bg-cathedral-bright h-2.5 w-2.5 rotate-45" />
					<div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
				</div>

				{/* What You Discover */}
				<section className="mx-auto max-w-3xl">
					<h2 className="cathedral-heading text-cathedral-dark text-center text-3xl font-semibold tracking-wide md:text-4xl">
						What You'll Discover
					</h2>

					<dl className="mt-12 space-y-10">
						<div>
							<dt className="cathedral-heading text-cathedral-dark text-2xl font-semibold leading-tight">
								Your Orientation
							</dt>
							<dd className="cathedral-body text-cathedral-brown mt-3 text-lg leading-relaxed md:text-xl">
								Interactive or Internalizing — how quickly you release what you
								take in. Not about energy or socializing. About the speed
								between perceiving and responding.
							</dd>
						</div>
						<div>
							<dt className="cathedral-heading text-cathedral-dark text-2xl font-semibold leading-tight">
								Your Temperament
							</dt>
							<dd className="cathedral-body text-cathedral-brown mt-3 text-lg leading-relaxed md:text-xl">
								Bold, Radiant, Immersive, or Level — the most stable dimension
								of who you are, grounded in four powers of the soul. Each
								carries both a disposition (what comes easily) and a distortion
								(what tends to go wrong).
							</dd>
						</div>
						<div>
							<dt className="cathedral-heading text-cathedral-dark text-2xl font-semibold leading-tight">
								Your Potential
							</dt>
							<dd className="cathedral-body text-cathedral-brown mt-3 text-lg leading-relaxed md:text-xl">
								Aesthetic, Kinesthetic, Noetic, or Empathic — what you do
								almost effortlessly while others work hard for the same result.
								Potential unrealized becomes restlessness. Potential cultivated
								becomes your distinctive contribution.
							</dd>
						</div>
						<div>
							<dt className="cathedral-heading text-cathedral-dark text-2xl font-semibold leading-tight">
								Your Character
							</dt>
							<dd className="cathedral-body text-cathedral-brown mt-3 text-lg leading-relaxed md:text-xl">
								Driven, Guarded, Attentive, or Detached — what was formed
								through family, culture, and early experiences. Unlike
								temperament, character can shift over time through decisions,
								relationships, and suffering.
							</dd>
						</div>
					</dl>
				</section>

				<div
					className="mx-auto flex w-full max-w-xl items-center gap-4"
					aria-hidden
				>
					<div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
					<div className="bg-cathedral-bright h-2.5 w-2.5 rotate-45" />
					<div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
				</div>

				{/* The Ikon Image */}
				<section className="mx-auto max-w-3xl text-center">
					<h2 className="cathedral-heading text-cathedral-dark text-3xl font-semibold tracking-wide md:text-4xl">
						The Ikon Image
					</h2>
					<p className="cathedral-body text-cathedral-brown mt-6 text-lg leading-relaxed md:text-xl">
						An <em>ikon</em> — in the ancient sense — is never flat. It exists
						in three dimensions: as itself, pointing toward what it images, and
						opening onto something deeper and more beautiful.
					</p>
					<p className="cathedral-body text-cathedral-brown mt-5 text-lg leading-relaxed md:text-xl">
						Your portrait integrates all three dimensions into a narrative — not
						a list of traits, not a type code. A living image that speaks to who
						you are, what you carry, and how you were formed.
					</p>
					<p className="cathedral-body text-cathedral-brown mt-5 text-base italic md:text-lg">
						"What particular image of the divine do you bear?"
					</p>
				</section>

				{/* CTA */}
				<section className="mx-auto w-full max-w-2xl text-center">
					<p className="cathedral-body text-cathedral-brown text-lg md:text-xl">
						{totalQuestions} questions · 20 minutes · AI-interpreted portrait
					</p>
					<Link
						to="/pricing"
						className="cathedral-heading border-cathedral-gold bg-cathedral-bright text-cathedral-deep mt-7 inline-block rounded-sm border-2 px-10 py-4 text-base tracking-[0.15em] uppercase transition-opacity hover:opacity-90"
					>
						Begin Your Journey — $5
					</Link>
					<p className="cathedral-body text-cathedral-brown mt-5 text-base md:text-lg">
						Full three-dimensional portrait for $5
					</p>

					<div className="border-cathedral-gold/20 bg-cathedral-card mt-10 rounded-xl border p-6 text-left shadow-sm">
						<h2 className="cathedral-heading text-cathedral-dark text-2xl font-semibold tracking-wide">
							Have a code?
						</h2>
						<p className="cathedral-body text-cathedral-brown mt-2 text-base">
							Enter it here if your assessment is included with a retreat,
							donor preview, beta invitation, or other Image and Likeness group.
						</p>
						<Form method="post" className="mt-5 flex flex-col gap-3 sm:flex-row">
							<label htmlFor="access-code" className="sr-only">
								Access code
							</label>
							<input
								id="access-code"
								name="code"
								type="text"
								autoComplete="off"
								placeholder="Enter access code"
								className="border-cathedral-gold/30 bg-cathedral-page text-cathedral-dark placeholder:text-cathedral-brown/60 min-h-12 flex-1 rounded-sm border px-4 text-base uppercase tracking-[0.12em]"
							/>
							<button
								type="submit"
								className="cathedral-heading border-cathedral-gold text-cathedral-gold hover:bg-cathedral-bright hover:text-cathedral-deep min-h-12 rounded-sm border-2 px-5 text-sm tracking-[0.14em] uppercase transition-colors"
							>
								Continue with code
							</button>
						</Form>
					</div>
				</section>

				<footer className="border-t border-cathedral-gold/20 pt-8">
					<div className="cathedral-body text-cathedral-brown flex flex-wrap items-center justify-center gap-6 text-sm md:text-base">
						<Link
							to="/privacy"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Privacy Policy
						</Link>
						<Link
							to="/tos"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Terms of Service
						</Link>
						<Link
							to="/support"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Support
						</Link>
					</div>
				</footer>
			</div>
		</main>
	)
}
